import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Database,
  Users,
  MessageSquare,
  Activity,
  BarChart3,
  Settings,
  RefreshCw,
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  Bot,
  Sparkles,
} from 'lucide-react';
import { useAppStore } from '../store/appStore';
import { apps as defaultApps } from '../config/apps';
import { App } from '../types';
import { AppIcon } from '../components/icons/AppIcons';

type TabType = 'apps' | 'users' | 'conversations' | 'activities' | 'analytics' | 'ai-agent';

export default function Admin() {
  const navigate = useNavigate();
  const { apps, userProfile } = useAppStore();
  const [activeTab, setActiveTab] = useState<TabType>('apps');
  const [isLoading, setIsLoading] = useState(false);
  const [editingApp, setEditingApp] = useState<App | null>(null);

  // AI Agent State
  const [agentPrompt, setAgentPrompt] = useState('');
  const [agentResponse, setAgentResponse] = useState('');
  const [isAgentLoading, setIsAgentLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState('general');

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'apps', label: 'Apps', icon: <Database className="w-4 h-4" /> },
    { id: 'users', label: 'Users', icon: <Users className="w-4 h-4" /> },
    { id: 'conversations', label: 'Conversations', icon: <MessageSquare className="w-4 h-4" /> },
    { id: 'activities', label: 'Activities', icon: <Activity className="w-4 h-4" /> },
    { id: 'analytics', label: 'Analytics', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'ai-agent', label: 'AI Agent', icon: <Bot className="w-4 h-4" /> },
  ];

  const agentTypes = [
    { id: 'general', name: 'General Assistant' },
    { id: 'recipe-analyst', name: 'Recipe Analyst' },
    { id: 'lead-gen', name: 'Lead Generation' },
    { id: 'email-writer', name: 'Email Copywriter' },
    { id: 'data-analyst', name: 'Data Analyst' },
    { id: 'code-helper', name: 'Code Helper' },
  ];

  const handleRefresh = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 1000);
  };

  const handleAgentSubmit = async () => {
    if (!agentPrompt.trim()) return;

    setIsAgentLoading(true);
    setAgentResponse('');

    try {
      // Use the Gemini API directly for demo
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=AIzaSyDhhzVXv1J2hKPU8aC_mN8H2RIsz9IA6pY`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{ role: 'user', parts: [{ text: agentPrompt }] }],
            generationConfig: { temperature: 0.7, maxOutputTokens: 2048 },
          }),
        }
      );

      const data = await response.json();
      const text = data.candidates?.[0]?.content?.parts?.[0]?.text || 'No response received';
      setAgentResponse(text);
    } catch (error) {
      setAgentResponse(`Error: ${error instanceof Error ? error.message : 'Failed to get response'}`);
    } finally {
      setIsAgentLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-recipe-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-recipe-card border-b border-recipe-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 rounded-lg hover:bg-recipe-bg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-recipe-text" />
              </button>
              <div className="flex items-center gap-3">
                <div className="bg-recipe-accent rounded-lg p-2">
                  <Settings className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-recipe-text">Admin Dashboard</h1>
                  <p className="text-xs text-recipe-text-muted">Database & AI Management</p>
                </div>
              </div>
            </div>

            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-recipe-accent text-recipe-bg rounded-lg font-medium hover:bg-recipe-accent/90 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-recipe-accent text-recipe-bg'
                  : 'bg-recipe-card border border-recipe-border text-recipe-text hover:border-recipe-accent'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-recipe-card border border-recipe-border rounded-xl p-6">
          {/* Apps Tab */}
          {activeTab === 'apps' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-recipe-text">Registered Apps</h2>
                <button className="flex items-center gap-2 px-4 py-2 bg-green-500/10 text-green-500 rounded-lg font-medium hover:bg-green-500/20 transition-colors">
                  <Plus className="w-4 h-4" />
                  Add App
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-recipe-border">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-recipe-text-muted">Icon</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-recipe-text-muted">Name</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-recipe-text-muted">Category</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-recipe-text-muted">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-recipe-text-muted">URL</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-recipe-text-muted">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {defaultApps.map((app) => (
                      <tr key={app.id} className="border-b border-recipe-border/50 hover:bg-recipe-bg/50">
                        <td className="py-3 px-4">
                          <div className="w-10 h-10 flex items-center justify-center bg-recipe-accent/10 rounded-lg text-recipe-accent">
                            <AppIcon name={app.icon} size={24} />
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-sm font-medium text-recipe-text">{app.name}</p>
                            <p className="text-xs text-recipe-text-muted">v{app.version}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 bg-recipe-bg rounded text-xs text-recipe-text-muted">
                            {app.category}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            app.status === 'active'
                              ? 'bg-green-500/10 text-green-500'
                              : app.status === 'development'
                              ? 'bg-blue-500/10 text-blue-500'
                              : 'bg-yellow-500/10 text-yellow-500'
                          }`}>
                            {app.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <a
                            href={app.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-recipe-accent hover:underline"
                          >
                            {app.url}
                          </a>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button className="p-2 rounded-lg hover:bg-recipe-bg transition-colors text-recipe-text-muted hover:text-recipe-accent">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button className="p-2 rounded-lg hover:bg-recipe-bg transition-colors text-recipe-text-muted hover:text-red-500">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div>
              <h2 className="text-lg font-bold text-recipe-text mb-6">User Management</h2>
              <div className="bg-recipe-bg rounded-lg p-8 text-center">
                <Users className="w-12 h-12 text-recipe-text-muted mx-auto mb-4" />
                <p className="text-recipe-text-muted">
                  User management will be available when Firebase Auth is configured
                </p>
              </div>
            </div>
          )}

          {/* Conversations Tab */}
          {activeTab === 'conversations' && (
            <div>
              <h2 className="text-lg font-bold text-recipe-text mb-6">Conversation History</h2>
              <div className="bg-recipe-bg rounded-lg p-8 text-center">
                <MessageSquare className="w-12 h-12 text-recipe-text-muted mx-auto mb-4" />
                <p className="text-recipe-text-muted">
                  Conversations are stored in Firestore when the database is connected
                </p>
              </div>
            </div>
          )}

          {/* Activities Tab */}
          {activeTab === 'activities' && (
            <div>
              <h2 className="text-lg font-bold text-recipe-text mb-6">Activity Log</h2>
              <div className="bg-recipe-bg rounded-lg p-8 text-center">
                <Activity className="w-12 h-12 text-recipe-text-muted mx-auto mb-4" />
                <p className="text-recipe-text-muted">
                  Activity tracking is synced to Firestore in real-time
                </p>
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div>
              <h2 className="text-lg font-bold text-recipe-text mb-6">Analytics Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-recipe-bg rounded-lg p-6">
                  <p className="text-sm text-recipe-text-muted mb-1">Total Apps</p>
                  <p className="text-3xl font-bold text-recipe-text">{defaultApps.length}</p>
                </div>
                <div className="bg-recipe-bg rounded-lg p-6">
                  <p className="text-sm text-recipe-text-muted mb-1">Active Sessions</p>
                  <p className="text-3xl font-bold text-recipe-text">3</p>
                </div>
                <div className="bg-recipe-bg rounded-lg p-6">
                  <p className="text-sm text-recipe-text-muted mb-1">AI Interactions</p>
                  <p className="text-3xl font-bold text-recipe-text">127</p>
                </div>
              </div>
            </div>
          )}

          {/* AI Agent Tab */}
          {activeTab === 'ai-agent' && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Sparkles className="w-6 h-6 text-recipe-accent" />
                <h2 className="text-lg font-bold text-recipe-text">AI Agent Console</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Agent Selection */}
                <div className="lg:col-span-1">
                  <label className="block text-sm font-medium text-recipe-text-muted mb-2">
                    Select Agent
                  </label>
                  <div className="space-y-2">
                    {agentTypes.map((agent) => (
                      <button
                        key={agent.id}
                        onClick={() => setSelectedAgent(agent.id)}
                        className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                          selectedAgent === agent.id
                            ? 'border-recipe-accent bg-recipe-accent/10 text-recipe-accent'
                            : 'border-recipe-border text-recipe-text hover:border-recipe-accent/50'
                        }`}
                      >
                        {agent.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Chat Interface */}
                <div className="lg:col-span-2">
                  <div className="bg-recipe-bg rounded-lg p-4 min-h-[300px] mb-4">
                    {agentResponse ? (
                      <div className="prose prose-invert max-w-none">
                        <pre className="whitespace-pre-wrap text-sm text-recipe-text font-sans">
                          {agentResponse}
                        </pre>
                      </div>
                    ) : (
                      <p className="text-recipe-text-muted text-center py-12">
                        Send a message to the AI agent
                      </p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={agentPrompt}
                      onChange={(e) => setAgentPrompt(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleAgentSubmit()}
                      placeholder="Ask the AI agent..."
                      className="flex-1 px-4 py-3 bg-recipe-bg border border-recipe-border rounded-lg text-recipe-text placeholder-recipe-text-muted focus:outline-none focus:ring-2 focus:ring-recipe-accent/50"
                    />
                    <button
                      onClick={handleAgentSubmit}
                      disabled={isAgentLoading || !agentPrompt.trim()}
                      className="px-6 py-3 bg-recipe-accent text-recipe-bg rounded-lg font-medium hover:bg-recipe-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isAgentLoading ? (
                        <RefreshCw className="w-5 h-5 animate-spin" />
                      ) : (
                        'Send'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
